import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { CreditCard, ArrowCounterClockwise, CheckCircle, ClockClockwise, Warning, ArrowsClockwise, MagnifyingGlass, Lifebuoy, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { FailedRefundsPanel } from '../components/FailedRefundsPanel';
import { de, enUS } from 'date-fns/locale';

const STATUS_PILL = {
  paid:               { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { en: 'paid',   de: 'bezahlt' } },
  pending:            { bg: 'bg-amber-100',   text: 'text-amber-700',   label: { en: 'pending', de: 'offen'  } },
  refunded:           { bg: 'bg-slate-200',   text: 'text-slate-700',   label: { en: 'refunded',de: 'erstattet'} },
  partially_refunded: { bg: 'bg-blue-100',    text: 'text-blue-700',    label: { en: 'partial',  de: 'teilweise'} },
  failed:             { bg: 'bg-red-100',     text: 'text-red-700',     label: { en: 'failed',   de: 'fehlgeschlagen'} },
};

const AdminPayments = () => {
  const { language, API } = useAuth();
  const L = (en, deu) => language === 'de' ? deu : en;
  const dateLocale = language === 'de' ? de : enUS;

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refundDialog, setRefundDialog] = useState(null); // {payment, amount, reason}
  // Filter chip: when the admin clicks a user's name in the table we scope
  // the list to that user instead of navigating away.
  const [filter, setFilter] = useState({ user_id: null, user_name: null, refno: null });
  const [searchInput, setSearchInput] = useState('');
  // Payment-type quick filter: 'all' | 'refunds' | 'paid' | 'pending'
  const [typeFilter, setTypeFilter] = useState('all');

  // Datatrans health snapshot + rescue tools
  const [health, setHealth] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [orphanDialog, setOrphanDialog] = useState(null); // {orphans:[], dry_run:true}
  const [refundingOrphans, setRefundingOrphans] = useState(false);
  // Preflight: count orphan refunds silently so the admin sees a badge on
  // the "Waisen-Refunds suchen" button without having to click it first.
  const [orphanCount, setOrphanCount] = useState(0);
  const [rowActioning, setRowActioning] = useState(null); // transaction_id currently in-flight
  const [backfilling, setBackfilling] = useState(false);
  // Diagnose single user — "I cancelled but see no refund" workflow (Oct 2026).
  const [diagEmail, setDiagEmail] = useState('');
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const runRefundDiagnosis = async () => {
    if (!diagEmail.trim()) return;
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const { data } = await API.get(`/admin/community/refund-diagnosis?email=${encodeURIComponent(diagEmail.trim())}`);
      setDiagResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('User not found', 'User nicht gefunden'));
    } finally { setDiagLoading(false); }
  };
  // How many days back the table pulls. Defaults to 90 (roughly 3 months)
  // which comfortably covers a full month plus buffer. Admins can extend to
  // 180 / 365 / "all" from the header dropdown when they need a bigger window
  // for reconciliation or accounting queries.
  const [rangeDays, setRangeDays] = useState(90);

  const runOrphanAction = async (transaction_id, action, label) => {
    const confirmMsg = {
      mark_manual: L(
        'Mark this payment as MANUALLY refunded (outside our system)? This writes a negative Kasse entry and removes it from the list.',
        'Diese Zahlung als MANUELL erstattet markieren (außerhalb System)? Schreibt einen negativen Kasse-Eintrag und entfernt sie aus der Liste.',
      ),
      ignore: L(
        'Hide this payment from the orphan-refund list forever?',
        'Diese Zahlung dauerhaft aus der Waisen-Liste ausblenden?',
      ),
      refund_one: L(
        'Refund this ONE payment via Datatrans right now?',
        'Nur DIESE eine Zahlung jetzt über Datatrans erstatten?',
      ),
    }[action];
    if (!window.confirm(confirmMsg)) return;
    setRowActioning(transaction_id);
    try {
      const { data } = await API.post('/admin/community/orphan-refunds/action', { transaction_id, action });
      if (data?.not_paid) toast.info(data.message); else toast.success(label);
      // Remove the row from the dialog immediately
      setOrphanDialog((prev) => prev ? { ...prev, orphans: prev.orphans.filter(o => o.transaction_id !== transaction_id) } : prev);
      await load();
      await loadHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Action failed', 'Aktion fehlgeschlagen'));
    } finally { setRowActioning(null); }
  };

  const runBackfillMatches = async () => {
    if (!window.confirm(
      language === 'de'
        ? 'Alle historischen Match-Zahlungen prüfen und in Kasse + Match-Status nachziehen? Idempotent — kann mehrfach laufen.'
        : 'Backfill every past match payment into Kasse + match paid flag? Idempotent — safe to re-run.'
    )) return;
    setBackfilling(true);
    try {
      const { data } = await API.post('/admin/datatrans/backfill-match-payments');
      toast.success(L(
        `Backfill: ${data.match_flag_flipped} match flags fixed, ${data.kasse_entries_created} Kasse entries created, ${data.kasse_already_booked} already booked`,
        `Backfill: ${data.match_flag_flipped} Match-Flags gesetzt, ${data.kasse_entries_created} Kasse-Einträge erstellt, ${data.kasse_already_booked} bereits gebucht`,
      ));
      await load();
      await loadHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Backfill failed', 'Backfill fehlgeschlagen'));
    } finally { setBackfilling(false); }
  };

  // Backfill refund Kasse rows — catches cases where a Datatrans refund went
  // through but the corresponding "Rückerstattung" line never landed in the
  // daily report (e.g. Kasse insert threw and the exception was swallowed).
  // Idempotent thanks to the datatrans_transaction_id dedup in the helper.
  const runBackfillRefunds = async () => {
    if (!window.confirm(L(
      'Scan every refunded Datatrans transaction and add any missing Rückerstattung entries to the Kasse? Idempotent.',
      'Alle refundierten Datatrans-Zahlungen prüfen und fehlende Rückerstattungs-Einträge in der Kasse nachtragen? Idempotent.',
    ))) return;
    setBackfilling(true);
    try {
      const { data } = await API.post('/admin/refunds/backfill-kasse');
      toast.success(L(
        `Refund backfill: ${data.inserted} entries created, ${data.skipped_existing} already booked (${data.scanned} scanned)`,
        `Refund-Backfill: ${data.inserted} neu, ${data.skipped_existing} bereits gebucht (${data.scanned} geprüft)`,
      ));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Backfill failed', 'Backfill fehlgeschlagen'));
    } finally { setBackfilling(false); }
  };

  const loadHealth = useCallback(async () => {
    try {
      const { data } = await API.get('/admin/datatrans/health');
      setHealth(data);
    } catch (_) { /* silent — health is optional */ }
  }, [API]);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data } = await API.post('/admin/datatrans/sweep-pending');
      toast.success(L(
        `Sweep done: ${data.checked} checked, ${data.reconciled_paid} paid, ${data.reconciled_failed} failed`,
        `Sweep fertig: ${data.checked} geprüft, ${data.reconciled_paid} als bezahlt erkannt, ${data.reconciled_failed} fehlgeschlagen`,
      ));
      await load();
      await loadHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Sweep failed', 'Sweep fehlgeschlagen'));
    } finally { setSweeping(false); }
  };

  const scanOrphans = async (dry = true) => {
    setScanning(true);
    try {
      const { data } = await API.post('/admin/community/orphan-refunds/scan', { dry_run: dry });
      setOrphanDialog({ orphans: data.orphans || [], dry_run: dry, refunded: data.refunded || 0 });
      setOrphanCount((data.orphans || []).length);
      if (!dry && data.refunded > 0) {
        toast.success(L(
          `Refunded ${data.refunded} orphan payment(s)`,
          `${data.refunded} Waisen-Zahlung(en) erstattet`,
        ));
        await load();
      } else if (dry && (data.orphans || []).length === 0) {
        toast.success(L('No orphans — all good!', 'Keine Waisen gefunden — alles gut!'));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Scan failed', 'Scan fehlgeschlagen'));
    } finally { setScanning(false); }
  };

  const confirmOrphanRefunds = async () => {
    setRefundingOrphans(true);
    try {
      const { data } = await API.post('/admin/community/orphan-refunds/scan', { dry_run: false });
      toast.success(L(
        `Refunded ${data.refunded || 0} orphan payment(s)`,
        `${data.refunded || 0} Waisen-Zahlung(en) erstattet`,
      ));
      const errs = data.errors || [];
      if (errs.length) {
        toast.error(L(
          `${errs.length} refund(s) failed: ${errs.map(e => `${e.user_name}: ${e.error}`).join(' | ')}`,
          `${errs.length} Erstattung(en) fehlgeschlagen: ${errs.map(e => `${e.user_name}: ${e.error}`).join(' | ')}`,
        ), { duration: 15000 });
        setOrphanDialog((prev) => prev ? { ...prev, orphans: errs } : prev);
        setOrphanCount(errs.length);
      } else {
        setOrphanDialog(null);
        setOrphanCount(0);
      }
      await load();
      await loadHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Refund failed', 'Erstattung fehlgeschlagen'));
    } finally { setRefundingOrphans(false); }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '5000' });
      if (rangeDays) params.set('days', String(rangeDays));
      const { data } = await API.get(`/payments?${params.toString()}`);
      setPayments(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally { setLoading(false); }
  }, [API, language, rangeDays]);

  // Silent preflight: run a dry-run orphan scan on mount so the admin sees
  // a badge warning them of refunds-that-didn't-fire-automatically without
  // first clicking the button (Oct 2026: Timur / Mark Evans case).
  const preflightOrphans = useCallback(async () => {
    try {
      const { data } = await API.post('/admin/community/orphan-refunds/scan', { dry_run: true });
      setOrphanCount((data.orphans || []).length);
    } catch (_) { /* best-effort — the manual button still works */ }
  }, [API]);

  useEffect(() => { load(); loadHealth(); preflightOrphans(); }, [load, loadHealth, preflightOrphans]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return format(new Date(iso), 'dd. MMM yyyy HH:mm', { locale: dateLocale }); }
    catch { return iso; }
  };

  const openRefund = (p) => setRefundDialog({ payment: p, amount: p.amount, reason: 'requested_by_customer' });
  const closeRefund = () => setRefundDialog(null);

  // Client-side filter — combines the "clicked-user" chip with the free-text
  // search box (matches name, email, Datatrans-ID, refno).
  const filteredPayments = payments.filter((p) => {
    if (filter.user_id && p.user_id !== filter.user_id) return false;
    if (filter.refno && p.refno !== filter.refno) return false;
    if (typeFilter === 'refunds' && !(p.refund_summary || p.status === 'refunded' || p.status === 'partially_refunded')) return false;
    if (typeFilter === 'paid' && p.status !== 'paid') return false;
    if (typeFilter === 'pending' && p.status !== 'pending') return false;
    if (searchInput.trim()) {
      const q = searchInput.trim().toLowerCase();
      const hay = [
        p.user_name, p.user_email, p.datatrans_transaction_id, p.refno,
        p.purpose, p.context?.label, p.context?.title,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // KPI totals from the visible list (filters honoured).
  const totals = filteredPayments.reduce((acc, p) => {
    const amt = Number(p.amount || 0);
    if (p.status === 'paid') acc.paid += amt;
    if (p.status === 'pending') acc.pending += amt;
    if (p.refund_summary) acc.refunded += Number(p.refund_summary.total || 0);
    if (p.status === 'refunded' && !p.refund_summary) acc.refunded += amt;
    return acc;
  }, { paid: 0, pending: 0, refunded: 0 });

  const downloadCsv = async () => {
    try {
      const { data, headers } = await API.get('/admin/payments/export.csv', { responseType: 'blob' });
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Try to pull the filename out of Content-Disposition, fall back to a sensible default
      const cd = headers?.['content-disposition'] || headers?.get?.('content-disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      a.download = match ? match[1] : `padelarena-datatrans-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(L('CSV downloaded', 'CSV heruntergeladen'));
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Download failed', 'Download fehlgeschlagen'));
    }
  };

  const submitRefund = async () => {
    const p = refundDialog.payment;
    const amt = parseFloat(refundDialog.amount);
    if (!amt || amt <= 0 || amt > p.amount) {
      toast.error(L('Invalid amount', 'Ungültiger Betrag'));
      return;
    }
    try {
      const isFull = Math.abs(amt - p.amount) < 0.01;
      const body = { reason: refundDialog.reason };
      if (!isFull) body.amount = amt;
      const { data } = await API.post(`/payments/${p.id}/refund`, body);
      toast.success(L(`Refunded ${data.amount} CHF`, `${data.amount} CHF erstattet`));
      closeRefund();
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : L('Refund failed', 'Erstattung fehlgeschlagen'));
    }
  };

  const purposeLabel = (p) => {
    // Prefer the enriched context.label (from backend join)
    if (p.context?.label) {
      const dt = [p.context.date, p.context.time].filter(Boolean).join(' · ');
      const title = p.context.title ? ` · ${p.context.title}` : '';
      const suffix = dt ? ` · ${dt}` : '';
      return `${p.context.label}${title}${suffix}`;
    }
    // Fallback for very old rows without any joinable id
    if (p.purpose === 'community_late_cancel_fee') return L('Mixin Late Cancel Fee', 'Mixin Späte Abmeldung');
    if (p.purpose === 'community_signup') return L('Mixin Signup', 'Mixin Anmeldung');
    if (p.purpose === 'training_program') return L('Training Program', 'Training');
    if (p.purpose === 'match' || p.match_id) return L('Open Match', 'Offenes Match');
    if (p.purpose === 'booking' || p.booking_id) return L('Court Booking', 'Court-Buchung');
    if (p.subscription_id) return L('Subscription', 'Abo');
    return L('Other', 'Sonstiges');
  };

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
            <CreditCard size={32} weight="duotone" className="text-emerald-500" />
            {L('Payments', 'Zahlungen')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {L('Datatrans transactions — issue full or partial refunds.', 'Datatrans-Transaktionen — Voll- oder Teilerstattungen direkt verbuchen.')}
          </p>
        </div>
        <Button
          onClick={downloadCsv}
          variant="outline"
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold self-start sm:self-auto"
          data-testid="download-csv-btn"
        >
          <DownloadSimple size={16} className="mr-2" />
          {L('Export CSV', 'CSV exportieren')}
        </Button>
      </div>

      <FailedRefundsPanel />

      {/* Datatrans Rescue Tools ------------------------------------------------ */}
      <div className="bg-gradient-to-br from-indigo-50 via-white to-blue-50 border border-indigo-200/60 rounded-2xl shadow-sm p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
            <Lifebuoy size={22} weight="duotone" className="text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-slate-900 text-lg">
              {L('Datatrans Rescue Tools', 'Datatrans Rettungswerkzeuge')}
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
              {L(
                'Recover from missed webhooks, stuck pending payments, and orphaned charges from cancelled events.',
                'Verpasste Webhooks, hängende Zahlungen und Waisen-Zahlungen von stornierten Events reparieren.',
              )}
            </p>
          </div>
        </div>

        {health && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-center">
            <div className="bg-white/70 rounded-lg p-3 border border-slate-200/50">
              <div className="text-xs text-slate-500 uppercase tracking-wide">{L('Pending', 'Offen')}</div>
              <div className="text-2xl font-bold text-slate-900" data-testid="dt-pending-count">{health.pending_count}</div>
            </div>
            <div className={`rounded-lg p-3 border ${health.stuck_count > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white/70 border-slate-200/50'}`}>
              <div className="text-xs text-slate-500 uppercase tracking-wide">{L('Stuck >5min', 'Hängend >5min')}</div>
              <div className={`text-2xl font-bold ${health.stuck_count > 0 ? 'text-amber-700' : 'text-slate-900'}`} data-testid="dt-stuck-count">{health.stuck_count}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 border border-slate-200/50">
              <div className="text-xs text-slate-500 uppercase tracking-wide">{L('Paid 24h', 'Bezahlt 24h')}</div>
              <div className="text-2xl font-bold text-emerald-700">{health.paid_last_24h}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 border border-slate-200/50">
              <div className="text-xs text-slate-500 uppercase tracking-wide">{L('Refunds 7d', 'Erstatt. 7T')}</div>
              <div className="text-2xl font-bold text-slate-900">{health.refunded_last_7d}</div>
            </div>
          </div>
        )}

        {health && !health.hmac_key_present && (
          <div className="mb-3 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900">
            <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5 text-amber-600" />
            <span>{L(
              'No DATATRANS_HMAC_KEY set — webhook signature verification is disabled. Add the sign2 key from Datatrans backoffice to harden production.',
              'Kein DATATRANS_HMAC_KEY gesetzt — Webhook-Signaturprüfung ist deaktiviert. sign2-Key im Datatrans-Backoffice generieren und hinterlegen.',
            )}</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={runSweep}
            disabled={sweeping}
            className="bg-indigo-500 hover:bg-indigo-600 text-white font-bold"
            data-testid="datatrans-sweep-btn"
          >
            <ArrowsClockwise size={16} className={`mr-2 ${sweeping ? 'animate-spin' : ''}`} />
            {sweeping
              ? L('Sweeping…', 'Läuft…')
              : L('Sweep pending payments', 'Offene Zahlungen prüfen')}
          </Button>
          <Button
            onClick={() => scanOrphans(true)}
            disabled={scanning}
            variant="outline"
            className={`relative font-bold ${
              orphanCount > 0
                ? 'border-amber-400 text-amber-800 hover:bg-amber-50 ring-2 ring-amber-200 ring-offset-1'
                : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50'
            }`}
            data-testid="datatrans-orphan-scan-btn"
          >
            <MagnifyingGlass size={16} className={`mr-2 ${scanning ? 'animate-pulse' : ''}`} />
            {scanning
              ? L('Scanning…', 'Sucht…')
              : L('Scan orphan refunds', 'Waisen-Refunds suchen')}
            {orphanCount > 0 && (
              <span
                className="absolute -top-2 -right-2 min-w-[22px] h-5 px-1 rounded-full bg-amber-500 text-white text-[11px] font-extrabold flex items-center justify-center shadow-md ring-2 ring-white"
                data-testid="orphan-refund-badge"
              >
                {orphanCount}
              </span>
            )}
          </Button>
          {/* Single-user refund-trace tool — paste an email, hit Enter, see
              every community-signup txn + exactly WHY the orphan scanner
              would or wouldn't surface it. Added Oct 2026 for the Timur /
              Mark Evans case where the admin couldn't find their refund. */}
          <div className="flex items-center gap-1" data-testid="refund-diagnosis-wrap">
            <input
              type="email"
              value={diagEmail}
              onChange={(e) => setDiagEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runRefundDiagnosis(); }}
              placeholder={L('User email to diagnose', 'Email für Diagnose')}
              className="h-9 px-3 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400 w-56"
              data-testid="refund-diagnosis-email"
            />
            <Button
              onClick={runRefundDiagnosis}
              disabled={diagLoading || !diagEmail.trim()}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50 font-bold"
              data-testid="refund-diagnosis-btn"
            >
              {diagLoading ? L('Checking…', 'Prüft…') : L('Diagnose', 'Diagnose')}
            </Button>
          </div>
          <Button
            onClick={runBackfillMatches}
            disabled={backfilling}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold"
            data-testid="datatrans-backfill-btn"
          >
            <ArrowsClockwise size={16} className={`mr-2 ${backfilling ? 'animate-spin' : ''}`} />
            {backfilling
              ? L('Backfilling…', 'Backfill läuft…')
              : L('Backfill missing Kasse', 'Fehlende Kasse-Einträge nachziehen')}
          </Button>
          <Button
            onClick={runBackfillRefunds}
            disabled={backfilling}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50 font-bold"
            data-testid="refund-backfill-btn"
          >
            <ArrowsClockwise size={16} className={`mr-2 ${backfilling ? 'animate-spin' : ''}`} />
            {backfilling
              ? L('Backfilling…', 'Backfill läuft…')
              : L('Backfill refund entries', 'Fehlende Rückerstattungen nachziehen')}
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2 leading-snug">
          {L(
            'Sweep polls Datatrans for every pending transaction. Scan finds payments where the user paid but is no longer signed up for the event, then refunds them. Backfill retro-writes any missing Kasse entry for historical online payments (matches, bookings, mixin).',
            'Sweep prüft Datatrans für jede offene Transaktion. Scan findet Zahlungen von Usern, die inzwischen abgemeldet sind – und erstattet sie automatisch. Backfill schreibt fehlende Kasse-Einträge für historische Online-Zahlungen nach (Matches, Buchungen, Mixin).',
          )}
        </p>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm italic">{L('Loading…', 'Lädt…')}</p>
      ) : payments.length === 0 ? (
        <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm p-12 text-center">
          <CreditCard size={48} weight="thin" className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-400 text-sm italic">{L('No payments yet.', 'Noch keine Zahlungen.')}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm overflow-hidden">
          {/* KPI strip: paid in / refunded out / pending — always visible so
              admins immediately see how much went in vs out for the selected
              time range + filters. */}
          <div className="grid grid-cols-3 border-b border-slate-100 divide-x divide-slate-100">
            <div className="p-4 text-center" data-testid="kpi-paid-in">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{L('Paid in', 'Eingenommen')}</p>
              <p className="text-lg sm:text-2xl font-extrabold text-emerald-600">{totals.paid.toFixed(2)} CHF</p>
            </div>
            <div className="p-4 text-center" data-testid="kpi-refunded-out">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{L('Refunded', 'Rückerstattet')}</p>
              <p className="text-lg sm:text-2xl font-extrabold text-rose-600">-{totals.refunded.toFixed(2)} CHF</p>
            </div>
            <div className="p-4 text-center" data-testid="kpi-pending">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{L('Pending', 'Offen')}</p>
              <p className="text-lg sm:text-2xl font-extrabold text-amber-600">{totals.pending.toFixed(2)} CHF</p>
            </div>
          </div>
          {/* Type-filter pills */}
          <div className="px-4 pt-3 flex flex-wrap gap-2 border-b border-slate-100 pb-3">
            {[
              { k: 'all',      de: 'Alle',            en: 'All' },
              { k: 'paid',     de: 'Nur bezahlt',     en: 'Paid only' },
              { k: 'refunds',  de: 'Nur Rückerstattungen', en: 'Refunds only' },
              { k: 'pending',  de: 'Nur offene',      en: 'Pending only' },
            ].map(t => (
              <button
                key={t.k}
                onClick={() => setTypeFilter(t.k)}
                className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${
                  typeFilter === t.k
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`type-filter-${t.k}`}
              >
                {L(t.en, t.de)}
              </button>
            ))}
          </div>
          {/* Search + filter chip bar */}
          <div className="px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder={L('Search name, email, Datatrans-ID or refno…', 'Name, Email, Datatrans-ID oder Refno suchen…')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9 border-slate-200"
                data-testid="payment-search"
              />
            </div>
            <select
              value={rangeDays || 'all'}
              onChange={(e) => setRangeDays(e.target.value === 'all' ? null : parseInt(e.target.value, 10))}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white text-slate-700"
              data-testid="payment-range-days"
              title={L('Time range', 'Zeitraum')}
            >
              <option value="30">{L('Last 30 days', 'Letzte 30 Tage')}</option>
              <option value="60">{L('Last 60 days', 'Letzte 60 Tage')}</option>
              <option value="90">{L('Last 90 days', 'Letzte 90 Tage')}</option>
              <option value="180">{L('Last 6 months', 'Letzte 6 Monate')}</option>
              <option value="365">{L('Last 12 months', 'Letzte 12 Monate')}</option>
              <option value="all">{L('All time', 'Gesamter Zeitraum')}</option>
            </select>
            {(filter.user_id || filter.refno) && (
              <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-1.5">
                <span className="text-xs text-slate-500">{L('Filter:', 'Filter:')}</span>
                <span className="text-sm font-bold text-indigo-800">
                  {filter.user_name || filter.refno}
                </span>
                <button
                  onClick={() => setFilter({ user_id: null, user_name: null, refno: null })}
                  className="text-indigo-500 hover:text-indigo-800 font-bold"
                  data-testid="clear-filter"
                >×</button>
              </div>
            )}
            <a
              href={filter.user_id ? `/admin/users?user_id=${filter.user_id}` : '#'}
              className={`text-xs font-semibold whitespace-nowrap ${filter.user_id ? 'text-slate-600 hover:text-indigo-600 underline' : 'text-slate-300 pointer-events-none'}`}
              data-testid="open-user-detail"
            >
              {L('Open user profile', 'User-Profil öffnen')} →
            </a>
          </div>

          <table className="w-full text-sm" data-testid="payments-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">{L('Date', 'Datum')}</th>
                <th className="text-left px-4 py-3">{L('Purpose', 'Zweck')}</th>
                <th className="text-left px-4 py-3">{L('Customer', 'Kunde')}</th>
                <th className="text-left px-4 py-3">{L('Datatrans-ID / Refno', 'Datatrans-ID / Refno')}</th>
                <th className="text-right px-4 py-3">{L('Amount', 'Betrag')}</th>
                <th className="text-center px-4 py-3">{L('Status', 'Status')}</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPayments.map(p => {
                const pill = STATUS_PILL[p.status] || STATUS_PILL.pending;
                const refundedTotal = (p.refunds || []).reduce((s, r) => s + (r.amount || 0), 0);
                const canRefund = p.status === 'paid' && p.datatrans_transaction_id;
                return (
                  <tr key={p.id} className="hover:bg-slate-50/60" data-testid={`payment-row-${p.id}`}>
                    <td className="px-4 py-3 text-slate-700">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3 text-slate-700">{purposeLabel(p)}</td>
                    <td className="px-4 py-3">
                      {p.user_name ? (
                        <button
                          type="button"
                          onClick={() => setFilter({ user_id: p.user_id, user_name: p.user_name, refno: null })}
                          className="text-slate-900 font-semibold hover:text-indigo-600 hover:underline text-left"
                          data-testid={`payment-user-${p.id}`}
                          title={L('Filter payments by this user', 'Nach diesem User filtern')}
                        >
                          {p.user_name}
                        </button>
                      ) : (
                        <span className="text-slate-400 text-xs">{p.user_id?.slice(0, 8)}…</span>
                      )}
                      {p.user_email && (
                        <div className="text-[11px] text-slate-400 truncate max-w-[220px]">{p.user_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {p.datatrans_transaction_id ? (
                        <div className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            onClick={() => { navigator.clipboard?.writeText(p.datatrans_transaction_id); toast.success(L('Copied', 'Kopiert')); }}
                            className="font-mono text-xs text-slate-700 hover:text-indigo-600 text-left"
                            title={L('Click to copy', 'Klick zum Kopieren')}
                            data-testid={`copy-tid-${p.id}`}
                          >
                            {p.datatrans_transaction_id}
                          </button>
                          {p.refno && (
                            <span className="font-mono text-[10px] text-slate-400 truncate max-w-[220px]" title={p.refno}>
                              {p.refno}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300 text-xs italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-slate-800 tabular-nums">{p.amount?.toFixed(2)} {(p.currency || 'CHF').toUpperCase()}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${pill.bg} ${pill.text}`}>
                        {p.status === 'paid' && <CheckCircle size={10} weight="fill" />}
                        {p.status === 'pending' && <ClockClockwise size={10} />}
                        {(p.status === 'refunded' || p.status === 'partially_refunded') && <ArrowCounterClockwise size={10} />}
                        {p.status === 'failed' && <Warning size={10} weight="fill" />}
                        {pill.label[language] || pill.label.en}
                        {refundedTotal > 0 && p.status !== 'paid' && (
                          <span className="ml-1 opacity-70">−{refundedTotal.toFixed(0)}</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canRefund ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openRefund(p)}
                          className="border-blue-200 text-blue-600 hover:bg-blue-50 text-xs"
                          data-testid={`refund-btn-${p.id}`}
                        >
                          <ArrowCounterClockwise size={12} className="mr-1" /> {L('Refund', 'Erstatten')}
                        </Button>
                      ) : p.status === 'partially_refunded' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openRefund({ ...p, amount: p.amount - refundedTotal })}
                          className="border-blue-200 text-blue-600 hover:bg-blue-50 text-xs"
                          data-testid={`refund-rest-btn-${p.id}`}
                        >
                          <ArrowCounterClockwise size={12} className="mr-1" /> {L(`Refund rest (${(p.amount - refundedTotal).toFixed(2)})`, `Rest erstatten (${(p.amount - refundedTotal).toFixed(2)})`)}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!refundDialog} onOpenChange={(o) => { if (!o) closeRefund(); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="refund-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-blue-700">
              <ArrowCounterClockwise size={20} weight="fill" /> {L('Issue Refund', 'Erstattung ausführen')}
            </DialogTitle>
            <DialogDescription>
              {refundDialog && L(
                `Original payment: ${refundDialog.payment.amount.toFixed(2)} CHF`,
                `Originalzahlung: ${refundDialog.payment.amount.toFixed(2)} CHF`
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="label-text">{L('Amount (CHF)', 'Betrag (CHF)')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max={refundDialog?.payment.amount}
                value={refundDialog?.amount ?? ''}
                onChange={(e) => setRefundDialog({ ...refundDialog, amount: e.target.value })}
                className="bg-slate-50 border-slate-200 mt-1"
                data-testid="refund-amount-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">{L('Leave full amount for full refund', 'Vollen Betrag = Vollerstattung')}</p>
            </div>
            <div>
              <Label className="label-text">{L('Reason', 'Grund')}</Label>
              <select
                value={refundDialog?.reason ?? ''}
                onChange={(e) => setRefundDialog({ ...refundDialog, reason: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm"
                data-testid="refund-reason-select"
              >
                <option value="requested_by_customer">{L('Customer request', 'Kundenwunsch')}</option>
                <option value="duplicate">{L('Duplicate', 'Doppelte Zahlung')}</option>
                <option value="fraudulent">{L('Fraudulent', 'Betrug')}</option>
              </select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeRefund} className="border-slate-200" data-testid="refund-cancel">
              {L('Cancel', 'Abbrechen')}
            </Button>
            <Button onClick={submitRefund} className="bg-blue-500 text-white hover:bg-blue-600 font-bold" data-testid="refund-confirm">
              {L('Issue Refund', 'Erstatten')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orphan-refund review dialog */}
      <Dialog open={!!orphanDialog} onOpenChange={(o) => { if (!o) setOrphanDialog(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl" data-testid="orphan-refund-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-indigo-700">
              <MagnifyingGlass size={20} weight="fill" /> {L('Orphan Refund Preview', 'Waisen-Erstattung Vorschau')}
            </DialogTitle>
            <DialogDescription>
              {orphanDialog && (orphanDialog.orphans.length === 0
                ? L('No orphan payments found — nothing to refund.', 'Keine Waisen-Zahlungen gefunden — nichts zu erstatten.')
                : L(
                    `${orphanDialog.orphans.length} payment(s). Choose per row: refund via Datatrans, mark as manually refunded (writes negative Kasse), or ignore (hide forever).`,
                    `${orphanDialog.orphans.length} Zahlung(en). Pro Zeile wählen: Erstatten über Datatrans, Manuell ✓ (schreibt negative Kasse), oder Ignorieren (dauerhaft ausblenden).`,
                  ))}
            </DialogDescription>
          </DialogHeader>
          {orphanDialog && orphanDialog.orphans.length > 0 && (
            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-2">{L('User', 'User')}</th>
                    <th className="text-left py-2 px-2">{L('Event', 'Event')}</th>
                    <th className="text-right py-2 px-2">{L('Amount', 'Betrag')}</th>
                    <th className="text-left py-2 px-2">{L('Paid At', 'Bezahlt')}</th>
                    <th className="text-center py-2 px-2">{L('Actions', 'Aktionen')}</th>
                  </tr>
                </thead>
                <tbody>
                  {orphanDialog.orphans.map((o) => {
                    const inFlight = rowActioning === o.transaction_id;
                    return (
                      <tr key={o.transaction_id} className={`border-t border-slate-100 ${inFlight ? 'opacity-50' : ''}`}>
                        <td className="py-2 px-2">
                          <div className="font-semibold">{o.user_name}</div>
                          <div className="text-slate-400 text-[10px]">{o.user_email}</div>
                        </td>
                        <td className="py-2 px-2 text-slate-700">
                          {o.event_title} <span className="text-slate-400">· {o.event_date}</span>
                        </td>
                        <td className="py-2 px-2 text-right font-mono font-bold text-slate-900">
                          {(o.amount || 0).toFixed(2)}
                        </td>
                        <td className="py-2 px-2 text-slate-500">{fmtDate(o.paid_at)}</td>
                        <td className="py-2 px-2">
                          <div className="flex flex-col sm:flex-row gap-1 justify-center">
                            <button
                              onClick={() => runOrphanAction(o.transaction_id, 'refund_one', L('Refunded via Datatrans', 'Über Datatrans erstattet'))}
                              disabled={inFlight}
                              className="px-2 py-1 text-[10px] font-bold rounded bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50"
                              title={L('Refund via Datatrans', 'Über Datatrans erstatten')}
                              data-testid={`orphan-refund-one-${o.transaction_id}`}
                            >
                              {L('Refund', 'Erstatten')}
                            </button>
                            <button
                              onClick={() => runOrphanAction(o.transaction_id, 'mark_manual', L('Marked as manually refunded', 'Als manuell erstattet markiert'))}
                              disabled={inFlight}
                              className="px-2 py-1 text-[10px] font-bold rounded bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
                              title={L('Mark as already refunded outside the system (Norbert case)', 'Bereits außerhalb System erstattet (Norbert-Fall)')}
                              data-testid={`orphan-mark-manual-${o.transaction_id}`}
                            >
                              {L('Manual ✓', 'Manuell ✓')}
                            </button>
                            <button
                              onClick={() => runOrphanAction(o.transaction_id, 'ignore', L('Hidden from list', 'Aus Liste entfernt'))}
                              disabled={inFlight}
                              className="px-2 py-1 text-[10px] font-bold rounded bg-slate-400 hover:bg-slate-500 text-white disabled:opacity-50"
                              title={L('Hide (Jo Crompton case — still on the event)', 'Ausblenden (Jo Crompton — noch im Event)')}
                              data-testid={`orphan-ignore-${o.transaction_id}`}
                            >
                              {L('Ignore', 'Ignorieren')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOrphanDialog(null)} className="border-slate-200" data-testid="orphan-cancel">
              {L('Close', 'Schliessen')}
            </Button>
            {orphanDialog && orphanDialog.orphans.length > 0 && (
              <Button
                onClick={confirmOrphanRefunds}
                disabled={refundingOrphans}
                className="bg-indigo-500 hover:bg-indigo-600 text-white font-bold"
                data-testid="orphan-refund-confirm"
              >
                <ArrowCounterClockwise size={16} className={`mr-2 ${refundingOrphans ? 'animate-spin' : ''}`} />
                {refundingOrphans
                  ? L('Refunding…', 'Erstatte…')
                  : L(`Refund all ${orphanDialog.orphans.length}`, `Alle ${orphanDialog.orphans.length} erstatten`)}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund-diagnosis result modal — shows every community-signup txn
          for the given user with a human-readable verdict + inline refund-
          one shortcut when a txn is scanner-eligible. */}
      <Dialog open={!!diagResult} onOpenChange={(o) => { if (!o) setDiagResult(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="refund-diagnosis-modal">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {L('Refund trace', 'Refund-Spur')}
              {diagResult && (
                <span className="ml-2 text-sm font-normal text-slate-500">
                  — {diagResult.user_name} · {diagResult.user_email}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {L(
                'All community-signup transactions for this user. The verdict column explains exactly why each one is or isn\'t being surfaced by the orphan scanner.',
                'Alle Community-Signup-Transaktionen dieses Users. Die Spalte „Verdikt" erklärt genau, warum jede Transaktion vom Waisen-Scanner (nicht) angezeigt wird.',
              )}
            </DialogDescription>
          </DialogHeader>

          {diagResult && diagResult.transactions.length === 0 && (
            <p className="text-slate-500 italic p-6 text-center">
              {L('No community-signup transactions found for this user.',
                 'Keine Community-Signup-Transaktionen für diesen User gefunden.')}
            </p>
          )}

          <div className="space-y-3">
            {diagResult?.transactions.map((tx) => {
              const verdictColor = {
                already_refunded:   'bg-green-50 border-green-300 text-green-800',
                forfeit:            'bg-slate-50 border-slate-300 text-slate-700',
                stuck_pending:      'bg-orange-50 border-orange-300 text-orange-800',
                paid_still_on_list: 'bg-blue-50 border-blue-300 text-blue-800',
                paid_orphan_eligible: 'bg-amber-50 border-amber-400 text-amber-900',
                paid_filtered_by_near_resignup: 'bg-rose-50 border-rose-300 text-rose-800',
                paid_but_ignored:   'bg-slate-50 border-slate-300 text-slate-700',
                paid_no_datatrans_id: 'bg-rose-50 border-rose-300 text-rose-800',
              }[tx.verdict] || 'bg-slate-50 border-slate-200 text-slate-700';
              const canForceRefund = tx.status === 'paid' && tx.has_datatrans_id && !tx.on_participant_list;
              return (
                <div key={tx.db_id} className={`border rounded-xl p-3 ${verdictColor}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm">
                        {tx.event_title || L('(Event deleted)', '(Event gelöscht)')} · {tx.event_date || '—'} {tx.event_start_time || ''}
                      </p>
                      <p className="text-xs mt-0.5 opacity-80">
                        {tx.amount?.toFixed ? tx.amount.toFixed(2) : tx.amount} CHF · {L('status', 'Status')}: <span className="font-mono font-semibold">{tx.status}</span> · txn: <span className="font-mono">{tx.transaction_id?.slice(-12) || '—'}</span>
                      </p>
                      <p className="text-xs mt-2 leading-relaxed">
                        <span className="font-bold uppercase tracking-wide">{L('Verdict', 'Verdikt')}: </span>{tx.verdict}
                      </p>
                      <p className="text-xs opacity-90">{tx.reason}</p>
                    </div>
                    {canForceRefund && (
                      <Button
                        size="sm"
                        onClick={() => runOrphanAction(tx.transaction_id, 'refund_one', L('Refund now', 'Jetzt erstatten'))}
                        disabled={rowActioning === tx.transaction_id}
                        className="bg-amber-500 hover:bg-amber-600 text-white font-bold shrink-0"
                        data-testid={`diag-refund-${tx.db_id}`}
                      >
                        {rowActioning === tx.transaction_id ? '…' : L('Refund', 'Erstatten')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDiagResult(null)} className="border-slate-200">
              {L('Close', 'Schliessen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayments;
